// src/sectors/pilates/sessions/components/SessionDetailDialog/SessionNotesCard.jsx
import React from 'react';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

import { SoftCard, toneColor } from './SessionDetailAtoms';
import { TR, upper, clean } from './sessionDetail.utils';

export function SessionNotesCard({
  notesLoading,
  notes,
  composerOpen,
  setComposerOpen,
  type,
  setType,
  text,
  setText,
  reminderMode,
  setReminderMode,
  beforeMin,
  setBeforeMin,
  reminderAtLocal,
  setReminderAtLocal,
  channel,
  setChannel,
  saving,
  onSave,
  onDelete,
  borderSoft,
}) {
  const theme = useMuiTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <SoftCard tone="mint">
      <MDBox
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
      >
        <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.9 }}>
          <MDBox
            sx={{
              width: 36,
              height: 36,
              borderRadius: '14px',
              border: borderSoft,
              display: 'grid',
              placeItems: 'center',
              background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.90)',
            }}
          >
            <Icon sx={{ fontSize: 18, color: theme.palette.success.main }}>sticky_note_2</Icon>
          </MDBox>

          <MDBox sx={{ display: 'grid', gap: 0.15 }}>
            <MDTypography variant="button" sx={{ fontWeight: 1000 }}>
              Notlar & Hatırlatmalar
            </MDTypography>
            <MDTypography variant="caption" sx={{ opacity: 0.75, fontWeight: 900 }}>
              {notesLoading ? 'yükleniyor…' : `${notes.length} kayıt`}
            </MDTypography>
          </MDBox>
        </MDBox>

        <Tooltip title={composerOpen ? 'Kapat' : 'Yeni ekle'}>
          <IconButton
            size="small"
            onClick={() => setComposerOpen((v) => !v)}
            sx={{
              borderRadius: 2,
              border: borderSoft,
              background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.85)',
            }}
          >
            <Icon fontSize="small">{composerOpen ? 'close' : 'add'}</Icon>
          </IconButton>
        </Tooltip>
      </MDBox>

      {composerOpen ? (
        <MDBox sx={{ display: 'grid', gap: 1, mt: 0.8 }}>
          <MDBox sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
            <Chip
              clickable
              onClick={() => setType('NOTE')}
              icon={<Icon>edit_note</Icon>}
              label="Not"
              variant={type === 'NOTE' ? 'filled' : 'outlined'}
              sx={{ fontWeight: 900 }}
            />
            <Chip
              clickable
              onClick={() => setType('REMINDER')}
              icon={<Icon>alarm</Icon>}
              label="Hatırlatma"
              variant={type === 'REMINDER' ? 'filled' : 'outlined'}
              sx={{ fontWeight: 900 }}
            />
          </MDBox>

          <TextField
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={type === 'REMINDER' ? 'Hatırlatma mesajı…' : 'Not ekleyin…'}
            fullWidth
            multiline
            minRows={3}
            size="small"
          />

          {type === 'REMINDER' ? (
            <MDBox sx={{ display: 'grid', gap: 1 }}>
              <MDBox sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
                <Chip
                  clickable
                  onClick={() => setReminderMode('BEFORE_MIN')}
                  icon={<Icon>schedule</Icon>}
                  label="Seansdan önce"
                  variant={reminderMode === 'BEFORE_MIN' ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 900 }}
                />
                <Chip
                  clickable
                  onClick={() => setReminderMode('AT_TIME')}
                  icon={<Icon>event</Icon>}
                  label="Tarih-saat"
                  variant={reminderMode === 'AT_TIME' ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 900 }}
                />
              </MDBox>

              {reminderMode === 'BEFORE_MIN' ? (
                <TextField
                  label="Kaç dakika önce?"
                  size="small"
                  type="number"
                  value={beforeMin}
                  onChange={(e) => setBeforeMin(e.target.value)}
                  inputProps={{ min: 1, step: 5 }}
                  helperText="Örn: 240 = 4 saat"
                />
              ) : (
                <TextField
                  label="Hatırlatma tarihi"
                  size="small"
                  type="datetime-local"
                  value={reminderAtLocal}
                  onChange={(e) => setReminderAtLocal(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  helperText="Seans başlangıcına göre fark otomatik hesaplanır."
                />
              )}

              <MDBox sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap' }}>
                <Chip
                  clickable
                  onClick={() => setChannel('IN_APP')}
                  icon={<Icon>notifications</Icon>}
                  label="Uygulama içi"
                  variant={channel === 'IN_APP' ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 900 }}
                />
                <Chip
                  clickable
                  onClick={() => setChannel('EMAIL')}
                  icon={<Icon>mail</Icon>}
                  label="E-posta"
                  variant={channel === 'EMAIL' ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 900 }}
                />
              </MDBox>

              <MDTypography variant="caption" sx={{ opacity: 0.75, fontWeight: 900 }}>
                * Hatırlatma sadece sana gidecek.
              </MDTypography>
            </MDBox>
          ) : null}

          <MDBox sx={{ display: 'flex', gap: 1, justifyContent: 'end', flexWrap: 'wrap' }}>
            <MDButton variant="outlined" color="secondary" onClick={() => setComposerOpen(false)}>
              Vazgeç
            </MDButton>
            <MDButton
              variant="gradient"
              color="info"
              disabled={saving || !clean(text)}
              onClick={onSave}
              sx={{ fontWeight: 900, borderRadius: '14px', textTransform: 'none' }}
            >
              {saving ? 'Kaydediliyor…' : 'Kaydet'}
            </MDButton>
          </MDBox>
        </MDBox>
      ) : null}

      <Divider sx={{ my: 1 }} />

      {!notes.length ? (
        <MDTypography variant="body2" sx={{ opacity: 0.7 }}>
          Henüz not yok.
        </MDTypography>
      ) : (
        <MDBox sx={{ display: 'grid', gap: 0.85 }}>
          {notes.map((n) => {
            const isRem = upper(n?.type) === 'REMINDER';
            const tone = isRem ? 'mint' : 'blue';
            const who = n?.createdByEmailSnapshot || n?.createdByNameSnapshot || '—';
            const when = n?.createdAt ? new Date(n.createdAt).toLocaleString() : '';
            const remLine = isRem
              ? `${TR.channel(n?.reminderChannel)} • ${
                  n?.reminderBeforeMinutes ? `${n.reminderBeforeMinutes} dk önce` : ''
                }`
              : null;

            return (
              <MDBox
                key={n.id}
                sx={{
                  borderRadius: '16px',
                  border: borderSoft,
                  background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.92)',
                  p: 1.05,
                  display: 'grid',
                  gap: 0.55,
                }}
              >
                <MDBox
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                  }}
                >
                  <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.8, minWidth: 0 }}>
                    <Icon sx={{ fontSize: 18, color: toneColor(theme, tone) }}>
                      {isRem ? 'alarm' : 'edit_note'}
                    </Icon>
                    <MDTypography variant="button" sx={{ fontWeight: 900 }}>
                      {TR.noteType(n.type)}
                    </MDTypography>
                    {isRem ? <Chip size="small" label={remLine} sx={{ fontWeight: 800 }} /> : null}
                  </MDBox>

                  <Tooltip title="Sil">
                    <IconButton size="small" onClick={() => onDelete?.(n.id)}>
                      <Icon fontSize="small">delete</Icon>
                    </IconButton>
                  </Tooltip>
                </MDBox>

                <MDTypography variant="body2" sx={{ fontWeight: 750, whiteSpace: 'pre-line' }}>
                  {n.note}
                </MDTypography>

                <MDTypography variant="caption" sx={{ opacity: 0.8, fontWeight: 800 }}>
                  {who} {when ? `• ${when}` : ''}
                </MDTypography>
              </MDBox>
            );
          })}
        </MDBox>
      )}
    </SoftCard>
  );
}
